import { newrelic } from "@jti/fe-clients";
import { type Address } from "@jti/fe-common";
import { useGetCustomerCartIdQuery, useSetBillingAddressOnCartMutation } from "@jti/store/slices/magentoApiSlice";
import { useTranslations } from "@jti/ui";

import { profileAddressToMagentoAddressAdapter } from "@checkout-v2/forms/ShippingForm/utils/profileAddressToMagentoAddressAdapter";
import { useCountry } from "@checkout-v2/shared/hooks/useCountry";
import { sanitizeAddressRegionData } from "@checkout-v2/shared/utils/sanitizeAddressRegionData";

const SET_BILLING_ADDRESS_ON_CART_MUTATION_FIXED_CACHE_KEY = "shared-set-billing-address-on-cart-mutation";

export const useSetBillingAddressOnCart = () => {
  const { __ } = useTranslations();
  const getCustomerCartIdQuery = useGetCustomerCartIdQuery();
  const countryQueryState = useCountry();

  const customerCartId = getCustomerCartIdQuery.data?.customerCart.id;

  const [setBillingAddressOnCartMutation, setBillingAddressOnCartMutationResult] = useSetBillingAddressOnCartMutation({
    fixedCacheKey: SET_BILLING_ADDRESS_ON_CART_MUTATION_FIXED_CACHE_KEY,
  });

  const setBillingAddressOnCart = async (address: Address) => {
    if (!customerCartId) {
      newrelic.noticeError("No cart ID available when trying to set billing address.");

      throw new Error(__("cart.checkout.shipping_form.error.set_billing_address_on_cart"));
    }

    const sanitizedAddress = sanitizeAddressRegionData(address, countryQueryState.data?.availableRegions);

    return setBillingAddressOnCartMutation({
      cartId: customerCartId,
      address: profileAddressToMagentoAddressAdapter(sanitizedAddress),
    });
  };

  return {
    setBillingAddressOnCart,
    setBillingAddressOnCartResult: setBillingAddressOnCartMutationResult,
  };
};
